class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery().length;

    if (!query) {
      event.preventDefault();       
      window.location.href = '/search?q=*&type=product';
      return;
    }

    if (this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      const cached = this.cachedResults[queryKey];
      this.renderSearchResults(cached.markup);
      this.applyTotalCount(cached.count);
      this.applyFullProducts(cached.products);
      return;
    }

    // live-match: also request collections so brand/vendor collections can be listed as "Designers"
    const predictiveSearchFetch = fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection&resources[limit]=10&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search').innerHTML
      );

    const totalCountFetch = this.fetchTotalResultsCount(searchTerm);

    Promise.all([predictiveSearchFetch, totalCountFetch])
      .then(([resultsMarkup, total]) => {
        const count = total ? total.count : null;
        const products = total ? total.products : null;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = { markup: resultsMarkup, count, products };
        });
        this.renderSearchResults(resultsMarkup);
        this.applyTotalCount(count);
        this.applyFullProducts(products);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  fetchTotalResultsCount(searchTerm) {
    const searchUrl = (typeof routes !== 'undefined' && routes.search_url) ? routes.search_url : '/search';
    return fetch(`${searchUrl}?q=${encodeURIComponent(searchTerm)}&type=product&section_id=predictive-search-count`)
      .then((response) => response.text())
      .then((html) => {
        const countEl = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('#shopify-section-predictive-search-count');
        const count = countEl ? countEl.textContent.trim() : null;
        if (count === null || count === '') {
          console.warn('[predictive-search] total count section not found - check that sections/predictive-search-count.liquid exists');
          return null;
        }
        let products = null;
        const holder = countEl.querySelector('[data-products]');
        if (holder) {
          try { products = JSON.parse(holder.getAttribute('data-products') || '[]'); } catch (e) { products = null; }
        }
        return { count, products };
      })
      .catch((err) => {
        console.warn('[predictive-search] failed to fetch total results count', err);
        return null;
      });
  }

  applyTotalCount(count) {
    if (count === null || count === undefined) return;
    this.allPredictiveSearchInstances.forEach((instance) => {
      const countSpans = instance.querySelectorAll('[data-predictive-search-total-count]');
      countSpans.forEach((span) => { span.textContent = count; });
      /* no "View all 0 items" button when the real search finds nothing */
      instance.querySelectorAll('.view-all-search').forEach((el) => { el.style.display = String(count) === '0' ? 'none' : ''; });
    });
  }

  /* Robin 26 Oct: the preview shows exactly what "View all" shows. Shopify's quick predictive search can return
     fewer products (e.g. "788": 1 of 10) or loose near-matches (e.g. "daddy" -> derby shoes), so the product list
     is rebuilt from the real search (first 10), reusing the markup of a predictive result so it looks the same.
     products === null means the real search could not be loaded: keep the predictive list as it is. */
  applyFullProducts(products) {
    if (!Array.isArray(products)) return;
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;
    const group = list.parentElement;
    if (!products.length) {
      /* the real search finds nothing: hide the near-matches and the "View all" button */
      if (group) group.style.display = 'none';
      this.querySelectorAll('.view-all-search').forEach((el) => { el.style.display = 'none'; });
      return;
    }
    if (group) group.style.display = '';
    this.querySelectorAll('.view-all-search').forEach((el) => { el.style.display = ''; });
    const template = list.firstElementChild;
    if (!template) return;
    const withWidth = (url, w) => url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
    const items = products.map((p, i) => {
      const li = template.cloneNode(true);
      li.id = 'predictive-search-option-product-' + (i + 1);
      li.setAttribute('aria-selected', 'false');
      const a = li.querySelector('a');
      if (a) a.setAttribute('href', p.url);
      const img = li.querySelector('img');
      if (img) {
        if (p.image) {
          img.src = withWidth(p.image, 480);
          img.srcset = [240, 360, 480, 640].map((w) => withWidth(p.image, w) + ' ' + w + 'w').join(', ');
          img.alt = p.title || '';
          if (p.ratio) img.setAttribute('height', Math.round(160 / p.ratio));
        } else {
          img.remove();
        }
      }
      const vendor = li.querySelector('.predictive-search__item-vendor');
      if (vendor) vendor.textContent = p.vendor || '';
      const heading = li.querySelector('.predictive-search__item-heading');
      if (heading) heading.textContent = p.title || '';
      li.querySelectorAll('.price').forEach((el) => el.remove());
      return li;
    });
    list.replaceChildren(...items);
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

// live-match (Robin 21 Oct): "Designers (N)" group in the mobile search drawer (#mobile-search-blocks).
// That drawer is rendered by the inline MobilePredictiveSearch script in sections/header.liquid, which only
// fetches products JSON. This adds the Designers group (from sections/predictive-search.liquid, so the same
// vendor match and locale-aware brand-collection URLs) above its "Products (N items found)" header.
(function () {
  function initMobileDesigners() {
    const box = document.querySelector('#mobile-search-blocks [data-predictive-search-results]');
    const input = document.querySelector('#mobile-search-blocks [data-predictive-search-input]');
    if (!box || !input || typeof routes === 'undefined' || !routes.predictive_search_url) return;

    const cache = {};
    const escapeHtml = (value) =>
      String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const fetchDesigners = (term) => {
      const key = term.toLowerCase();
      if (!cache[key]) {
        cache[key] = fetch(
          `${routes.predictive_search_url}?q=${encodeURIComponent(term)}&resources[type]=collection&resources[limit]=10&section_id=predictive-search`
        )
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const css = doc.querySelector('link[data-lm-designers-css]');
            if (css && !document.querySelector('head link[data-lm-designers-css]')) {
              document.head.appendChild(css.cloneNode());
            }
            return Array.from(doc.querySelectorAll('[data-lm-designers] a[data-lm-designer]')).map((link) => ({
              url: link.getAttribute('href'),
              name: link.textContent.trim(),
            }));
          })
          .catch(() => {
            delete cache[key];
            return [];
          });
      }
      return cache[key];
    };

    const update = () => {
      const term = input.value.trim();
      const current = box.querySelector('.lm-mobile-designers');
      if (term.length < 2) {
        if (current) current.remove();
        return;
      }
      if (current && current.dataset.term === term) return;

      fetchDesigners(term).then((designers) => {
        if (input.value.trim() !== term) return;
        const existing = box.querySelector('.lm-mobile-designers');
        if (existing && existing.dataset.term === term) return;
        if (existing) existing.remove();
        if (!designers.length) return;

        const group = document.createElement('div');
        group.className = 'lm-mobile-designers';
        group.dataset.term = term;
        group.innerHTML =
          `<div class="serach-drawer-details"><p class="search-result-count"><upper>Designers</upper> (${designers.length})</p></div>` +
          `<ul class="lm-mobile-designers__list">${designers
            .map(
              (designer) =>
                `<li><a class="lm-mobile-designers__link" href="${escapeHtml(designer.url)}"><span class="mobile-search-title">${escapeHtml(designer.name)}</span></a></li>`
            )
            .join('')}</ul>`;
        box.prepend(group);
      });
    };

    input.addEventListener('input', update);
    new MutationObserver(update).observe(box, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileDesigners);
  } else {
    initMobileDesigners();
  }
})();
